import { useEffect, useRef, useState } from 'react';
import { formatAgo, formatNumber, SYNC_LABELS, syncState } from './dashboardData';

/** Width of a container, kept in sync with resizes. */
function useWidth(initial = 600) {
  const ref = useRef(null);
  const [width, setWidth] = useState(initial);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.max(240, Math.floor(entry.contentRect.width)));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

/** Bar with only the data end rounded (4px), anchored square to the baseline. */
function barPath(x, y, w, h, r = 4) {
  if (h <= 0) return '';
  const radius = Math.min(r, w / 2, h);
  return [
    `M${x},${y + h}`,
    `V${y + radius}`,
    `Q${x},${y} ${x + radius},${y}`,
    `H${x + w - radius}`,
    `Q${x + w},${y} ${x + w},${y + radius}`,
    `V${y + h}`,
    'Z',
  ].join(' ');
}

function niceMax(value) {
  if (value <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const steps = [1, 2, 2.5, 5, 10];
  for (const step of steps) {
    if (step * magnitude >= value) return step * magnitude;
  }
  return 10 * magnitude;
}

/**
 * Single-series column chart with a hover/focus tooltip.
 * points: [{ key, label, rangeLabel, scans, units, future, current }]
 */
export function ActivityChart({ points, labelEvery = 3, emptyText }) {
  const [containerRef, width] = useWidth();
  const [active, setActive] = useState(null);

  const height = 220;
  const margin = { top: 18, right: 8, bottom: 26, left: 40 };
  const plotW = width - margin.left - margin.right;
  const plotH = height - margin.top - margin.bottom;

  const max = niceMax(Math.max(1, ...points.map((p) => p.scans)));
  const ticks = [0, max / 2, max];
  const slot = plotW / points.length;
  const gap = Math.max(2, Math.min(8, slot * 0.25));
  const barW = Math.max(2, slot - gap);
  const peak = points.reduce(
    (best, p, i) => (p.scans > (best ? best.p.scans : 0) ? { p, i } : best),
    null,
  );
  const total = points.reduce((sum, p) => sum + p.scans, 0);
  const activePoint = active != null ? points[active] : null;

  const y = (value) => margin.top + plotH - (value / max) * plotH;

  return (
    <div className="activity-chart" ref={containerRef}>
      {total === 0 && emptyText ? (
        <p className="activity-chart__empty">{emptyText}</p>
      ) : null}
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`Scans per period. Total ${formatNumber(total)}.`}
        onMouseLeave={() => setActive(null)}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={margin.left}
              x2={width - margin.right}
              y1={y(tick)}
              y2={y(tick)}
              className={tick === 0 ? 'activity-chart__baseline' : 'activity-chart__grid'}
            />
            <text
              x={margin.left - 8}
              y={y(tick)}
              dy="0.32em"
              textAnchor="end"
              className="activity-chart__tick"
            >
              {formatNumber(tick)}
            </text>
          </g>
        ))}

        {points.map((point, index) => {
          const x = margin.left + index * slot + gap / 2;
          const barH = (point.scans / max) * plotH;
          const isActive = active === index;
          return (
            <g key={point.key}>
              {point.future ? (
                <rect
                  x={x}
                  y={margin.top + plotH - 3}
                  width={barW}
                  height={3}
                  rx={1.5}
                  className="activity-chart__future"
                />
              ) : (
                <path
                  d={barPath(x, y(point.scans), barW, barH)}
                  className={`activity-chart__bar${isActive ? ' is-active' : ''}${
                    active != null && !isActive ? ' is-dimmed' : ''
                  }`}
                />
              )}
              {index % labelEvery === 0 || point.current ? (
                <text
                  x={x + barW / 2}
                  y={height - 8}
                  textAnchor="middle"
                  className={`activity-chart__tick${point.current ? ' is-current' : ''}`}
                >
                  {point.current ? point.currentLabel || 'Now' : point.label}
                </text>
              ) : null}
              {/* Hit target: the whole column, taller and wider than the bar. */}
              <rect
                x={margin.left + index * slot}
                y={margin.top}
                width={slot}
                height={plotH}
                fill="transparent"
                tabIndex={point.future ? -1 : 0}
                onMouseEnter={() => !point.future && setActive(index)}
                onFocus={() => !point.future && setActive(index)}
                onBlur={() => setActive(null)}
                aria-label={`${point.rangeLabel}: ${formatNumber(point.scans)} scans`}
              />
            </g>
          );
        })}

        {peak && peak.p.scans > 0 && active == null ? (
          <text
            x={margin.left + peak.i * slot + slot / 2}
            y={y(peak.p.scans) - 6}
            textAnchor="middle"
            className="activity-chart__peak"
          >
            {formatNumber(peak.p.scans)}
          </text>
        ) : null}
      </svg>

      {activePoint ? (
        <div
          className="chart-tooltip"
          style={{
            left: Math.min(
              Math.max(margin.left + active * slot + slot / 2, 80),
              width - 80,
            ),
            top: Math.max(0, y(activePoint.scans) - 64),
          }}
          role="status"
        >
          <strong>{activePoint.rangeLabel}</strong>
          <span>
            <i className="chart-tooltip__swatch" /> {formatNumber(activePoint.scans)} scans
          </span>
          <span className="chart-tooltip__muted">
            {formatNumber(activePoint.units)} units counted
          </span>
        </div>
      ) : null}

      <table className="visually-hidden">
        <caption>Scans per period</caption>
        <thead>
          <tr>
            <th scope="col">Period</th>
            <th scope="col">Scans</th>
            <th scope="col">Units</th>
          </tr>
        </thead>
        <tbody>
          {points
            .filter((p) => !p.future)
            .map((p) => (
              <tr key={p.key}>
                <th scope="row">{p.rangeLabel}</th>
                <td>{p.scans}</td>
                <td>{p.units}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

/** Tiny trend line for the live board (scans per hour, last 8 h). */
export function Sparkline({ values, width = 112, height = 30, label }) {
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? (width - 4) / (values.length - 1) : 0;
  const points = values.map((value, index) => [
    2 + index * step,
    height - 3 - (value / max) * (height - 6),
  ]);
  const line = points.map(([x, py], i) => `${i ? 'L' : 'M'}${x},${py}`).join(' ');
  const area = `${line} L${points[points.length - 1][0]},${height} L${points[0][0]},${height} Z`;
  const [lastX, lastY] = points[points.length - 1];

  return (
    <svg
      className="sparkline"
      width={width}
      height={height}
      role="img"
      aria-label={label}
    >
      <path d={area} className="sparkline__area" />
      <path d={line} className="sparkline__line" />
      <circle cx={lastX} cy={lastY} r={3} className="sparkline__dot" />
    </svg>
  );
}

/** Coverage bar: share of the audit's product list scanned at least once. */
export function CoverageMeter({ percent }) {
  return (
    <div
      className="coverage-meter"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-label="Product list covered"
    >
      <div className="coverage-meter__fill" style={{ width: `${Math.min(100, percent)}%` }} />
    </div>
  );
}

const SYNC_ICONS = {
  fresh: '●',
  late: '◐',
  stale: '○',
};

/** One dot per phone, coloured and shaped by how recently it synced. */
export function PhoneDots({ phones }) {
  const [open, setOpen] = useState(null);

  if (!phones.length) {
    return <span className="phone-dots__none">No phone has synced yet</span>;
  }

  const shown = phones.slice(0, 12);
  const extra = phones.length - shown.length;

  return (
    <div className="phone-dots" onMouseLeave={() => setOpen(null)}>
      {shown.map((phone, index) => {
        const state = syncState(phone.seconds_since_sync);
        const name = phone.device_label || phone.device_id;
        return (
          <span key={`${phone.device_id}-${index}`} className="phone-dots__item">
            <button
              type="button"
              className={`phone-dot phone-dot--${state}`}
              aria-label={`${name}: ${SYNC_LABELS[state]}, last synced ${formatAgo(phone.seconds_since_sync)}`}
              onMouseEnter={() => setOpen(index)}
              onFocus={() => setOpen(index)}
              onBlur={() => setOpen(null)}
            >
              <span aria-hidden="true">{SYNC_ICONS[state]}</span>
            </button>
            {open === index ? (
              <span className="chart-tooltip chart-tooltip--phone" role="status">
                <strong>{name}</strong>
                {phone.last_scanned_by ? <span>Last used by {phone.last_scanned_by}</span> : null}
                <span>Synced {formatAgo(phone.seconds_since_sync)}</span>
                <span className="chart-tooltip__muted">
                  {formatNumber(phone.scans)} scans received
                </span>
              </span>
            ) : null}
          </span>
        );
      })}
      {extra > 0 ? <span className="phone-dots__more">+{extra}</span> : null}
    </div>
  );
}

/** Ranked horizontal bars with the value at the end of each bar. */
export function RankedBars({ rows, valueKey = 'scans', unit = 'scans' }) {
  const max = Math.max(1, ...rows.map((row) => Number(row[valueKey]) || 0));
  return (
    <ol className="ranked-bars">
      {rows.map((row) => {
        const value = Number(row[valueKey]) || 0;
        return (
          <li key={row.name} className="ranked-bars__row">
            <span className="ranked-bars__name">{row.name}</span>
            <span className="ranked-bars__track">
              <span
                className="ranked-bars__fill"
                style={{ width: `${Math.max(2, (value / max) * 100)}%` }}
              />
            </span>
            <span className="ranked-bars__value">
              {formatNumber(value)}
              <span className="visually-hidden"> {unit}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
